export const jobs = [
  {
    title: 'Account Manager (Sales)',
    description: (
      <div className="prose max-w-none !text-black prose-h2:mb-3 prose-h2:mt-8 prose-h2:text-xl prose-h2:font-semibold prose-h2:leading-8">
        <p>
          We are seeking an experienced E-commerce Marketing Sales Specialist to
          join our dynamic team. We are looking for someone with a proven track
          record in e-commerce marketing sales, with a deep understanding of
          online business dynamics and digital marketing strategies. This role
          requires expertise in PPC, SEO, and social media marketing, along with
          proficiency in Shopify.
        </p>

        <p>
          <strong>The ideal candidate</strong> is someone who:
        </p>
        <ul>
          <li>Has sold e-commerce marketing services (not just products).</li>
          <li>
            Understands how to speak to DTC brands, Shopify store owners, etc.
          </li>
          <li>
            Can walk into a conversation and say, “Here’s how we’ll increase
            your ROAS, here’s how we’ll structure your funnel, here’s how our
            team delivers that.”
          </li>
        </ul>

        <h2>Key Responsibilities:</h2>
        <ul>
          <li>
            Identify and engage potential clients who require e-commerce web
            design and marketing services. Build and maintain strong, long-term
            relationships to understand their business needs and objectives.
          </li>
          <li>
            Develop and implement effective sales strategies tailored to the
            e-commerce sector, focusing on converting leads into satisfied
            clients.
          </li>
          <li>
            Provide expert consultation to clients on e-commerce marketing
            solutions, including website design, PPC campaigns, SEO
            optimization, and social media strategies.
          </li>
          <li>
            Conduct thorough market research to identify trends, opportunities,
            and competitive landscape within the e-commerce industry.
          </li>
          <li>
            Prepare and deliver compelling proposals and presentations that
            effectively communicate our services and value proposition to
            potential clients.
          </li>
          <li>
            Work closely with our design, marketing, and development teams to
            ensure client requirements are accurately translated into
            deliverable solutions.
          </li>
          <li>
            Monitor and report on sales performance metrics, adjusting
            strategies as necessary to achieve targets.
          </li>
        </ul>
        <h2>Qualifications:</h2>
        <ul>
          <li>
            Minimum of 3-5 years of proven experience in e-commerce marketing
            sales. Candidates without specific e-commerce sales experience will
            not be considered.
          </li>
          <li>
            In-depth knowledge and hands-on experience with PPC, SEO, and social
            media marketing strategies and tools.
          </li>
          <li>
            Strong understanding of the Shopify platform, including its
            features, functionalities, and best practices.
          </li>
          <li>
            Excellent verbal and written communication skills, with the ability
            to articulate complex concepts clearly and persuasively.
          </li>
          <li>
            Strong analytical skills to assess market trends, campaign
            performance, and sales data to inform strategies.
          </li>
          <li>
            Demonstrated ability to understand and address client needs,
            providing solutions that drive client satisfaction and business
            growth.
          </li>
          <li>
            Proven ability to work independently, manage multiple priorities,
            and achieve sales targets.
          </li>
        </ul>
        <h2>Preferred Qualifications:</h2>
        <ul>
          <li>
            Bachelor’s degree in Marketing, Business Administration, or a
            related field.
          </li>
          <li>
            Familiarity with web design principles and the ability to understand
            technical aspects of e-commerce platforms.
          </li>
          <li>
            Established network within the e-commerce industry and a track
            record of leveraging relationships to drive sales.
          </li>
        </ul>
        <h2>Why Join Us:</h2>
        <ul>
          <li>
            Be part of a forward-thinking team that values creativity and
            innovation in delivering top-notch e-commerce solutions.
          </li>
          <li>
            Opportunities for continuous learning and career advancement in the
            rapidly evolving e-commerce industry.
          </li>
          <li>
            Work in a supportive and collaborative environment where your
            contributions are valued and recognized.
          </li>
        </ul>
        <p>
          If you are a results-driven professional with a passion for e-commerce
          marketing sales and meet the qualifications outlined above, we invite
          you to apply and join our team in driving success for our clients and
          our agency.
        </p>
      </div>
    ),
    slug: 'account-manager-sales',
  },
  {
    title: 'Full Stack Developer',
    description: (
      <div className="prose max-w-none !text-black prose-h2:mb-3 prose-h2:mt-8 prose-h2:text-xl prose-h2:font-semibold prose-h2:leading-8">
        <p>
          We are looking for a skilled and versatile Full Stack Developer to
          build robust, scalable ecommerce websites on platforms like Shopify
          and BigCommerce, and non-ecommerce sites using Next.js. This role
          involves working on both client-facing projects and internal systems.
          You&apos;ll work on large codebases, refactoring and streamlining them into
          clean, maintainable, and scalable code to ensure long-term success.
        </p>
        <h2>Key Responsibilities</h2>
        <ul>
          <li>
            Build and deploy custom eCommerce stores on Shopify and BigCommerce
            that are tailored to meet client requirements, including full data
            management.
          </li>
          <li>
            Develop high-performance non-eCommerce websites using Next.js,
            emphasizing clean code practices, exceptional user experience, and
            rapid load times.
          </li>
          <li>
            Architect, build, and maintain internal systems with Next.js,
            including integration with databases and implementation of cron jobs
            for scheduled tasks.
          </li>
          <li>
            Refactor and optimize large codebases to improve maintainability,
            scalability, and performance, ensuring adherence to modern
            development standards.
          </li>
          <li>
            Build and maintain WordPress sites, including Divi theme, installing
            and managing plugins, regular site updates, and implementing ongoing
            improvements.
          </li>
          <li>
            Execute comprehensive PageSpeed Insights optimization strategies
            across all projects to ensure consistent rapid load times and
            superior performance.
          </li>
          <li>
            Collaborate with cross-functional teams, including design,
            marketing, to ensure seamless integration of systems and alignment
            with business goals.
          </li>
          <li>
            Participate actively in code reviews and technical discussions,
            contributing to the establishment of best practices and robust
            development processes.
          </li>
        </ul>
        <h2>Qualifications</h2>
        <ul>
          <li>
            Strong proficiency in Next.js, React.js, HTML, CSS, JavaScript,
            TypeScript, REST APIs, and GraphQL.
          </li>
          <li>
            Strong problem-solving skills, attention to detail, and the ability
            to thrive in a collaborative, fast-paced environment.
          </li>
          <li>
            Experience building eCommerce stores on Shopify and BigCommerce from
            start to finish.
          </li>
          <li>
            Demonstrated ability to develop high-performance websites and web
            applications with clean, maintainable code.
          </li>
          <li>
            Proven expertise in working with and refactoring large codebases to
            achieve scalability and maintainability.
          </li>
          <li>
            Skilled in building and integrating REST APIs using Next.js,
            Node.js, and Express.js.
          </li>
          <li>
            Familiarity with database systems including MongoDB and PostgreSQL,
            and modern deployment platforms like Vercel.
          </li>
          <li>
            Understanding of serverless architectures, including Vercel
            Serverless and Edge Networks, with experience in AI SDK integration.
          </li>
          <li>
            Well-versed in networking and security protocols including HTTP,
            SFTP, SSH, SMTP, DNS, SSL, and WebDAV.
          </li>
          <li>
            Experience with OpenAI APIs, version control tools (Git, GitHub),
            SEO best practices, and graphic design fundamentals.
          </li>
          <li>
            Solid understanding of pagespeed optimization techniques and overall
            web performance best practices.
          </li>
          <li>
            Excellent problem-solving skills with a keen attention to detail in
            a fast-paced, innovative environment.
          </li>
        </ul>
      </div>
    ),
    slug: 'full-stack-developer',
  },
  {
    title: 'Digital Marketing Specialist',
    description: (
      <div className="prose max-w-none !text-black prose-h2:mb-3 prose-h2:mt-8 prose-h2:text-xl prose-h2:font-semibold prose-h2:leading-8">
        <p>
          The Digital Marketing Specialist for Ventura will be responsible for
          developing and executing integrated, multi-channel campaigns that
          drive traffic, increase conversions, and elevate the digital presence
          of our eCommerce clients. The role requires a dynamic individual who
          can collaborate seamlessly with creative and development teams to
          craft compelling content and optimize digital channels. The selected
          candidate should also have intermediate knowledge with Adobe suite
          (Photoshop, Illustrator, XD) or Canva to create supplemental graphics
          toward campaigns.
        </p>
        <h2>Key Responsibilities & Duties</h2>
        <ul>
          <li>
            <strong>Strategic Campaign Development:</strong>
            <ul>
              <li>
                Plan, execute, and optimize multi-channel digital marketing
                campaigns (search, social, email, SMS, and display) to drive
                qualified traffic and increase conversions.
              </li>
              <li>
                Develop and manage content calendars for blog posts, social
                media, email newsletters, and eCommerce landing pages that
                engage potential customers and support SEO efforts. Work closely
                with developers and designers to produce high-quality,
                conversion-optimized content.
              </li>
              <li>
                Plan and execute social media strategies to build brand
                awareness, engage with the community, and drive traffic to
                eCommerce sites and grow followers.
              </li>
              <li>
                Test and analyze various conversion funnels, landing pages, and
                user interfaces to improve the user journey and increase
                conversion rates.
              </li>
            </ul>
          </li>
          <li>
            <strong>Analytics & Data-Driven Reporting:</strong>
            <ul>
              <li>
                Utilize tools such as Google Analytics, Google Tag Manager, Meta
                Ads Manager to track website performance, campaign
                effectiveness, and user behavior.
              </li>
              <li>
                Generate regular performance reports and provide actionable
                insights to optimize ongoing campaigns.
              </li>
              <li>
                Monitor budgets, analyze performance metrics, adjust bids/target
                audience and ad creative for maximum ROI.
              </li>
            </ul>
          </li>
          <li>
            <strong>Continuous Improvement & Innovation:</strong>
            <ul>
              <li>
                Stay current with digital marketing trends, emerging
                technologies, and new industry practices.
              </li>
              <li>
                Identify opportunities for improvement and contribute to the
                refinement of our digital marketing strategies.
              </li>
            </ul>
          </li>
        </ul>
        <h2>Required Qualifications</h2>
        <ul>
          <li>
            <strong>Education & Experience:</strong>
            <ul>
              <li>
                Bachelor’s degree in Marketing, Business, Communications, or a
                related field.
              </li>
              <li>
                2+ years of experience in digital marketing, preferably within
                an eCommerce or agency setting.
              </li>
            </ul>
          </li>
          <li>
            <strong>Technical Skills & Tools Expertise:</strong>
            <ul>
              <li>
                Proficiency in digital marketing tools such as Google Analytics,
                Google Ads, Meta Business Manager, SEMrush, Ahrefs, etc.
              </li>
              <li>
                Strong understanding of SEO, SEM, content marketing, and social
                media advertising.
              </li>
              <li>
                Experience with conversion rate optimization (CRO) techniques
                and A/B testing.
              </li>
              <li>
                Ability to work in graphics programs like Adobe Photoshop, XD,
                and Canva.
              </li>
            </ul>
          </li>
          <li>
            <strong>Analytical & Data-Driven Mindset:</strong>
            <ul>
              <li>
                Ability to analyze data, generate insights, and modify
                strategies to drive better results.
              </li>
              <li>
                Proficient in using marketing automation and CRM platforms.
              </li>
            </ul>
          </li>
          <li>
            <strong>Communication & Interpersonal Skills:</strong>
            <ul>
              <li>Excellent written and verbal communication skills.</li>
              <li>
                Ability to work collaboratively in a fast-paced,
                cross-functional team environment.
              </li>
              <li>
                Strong project management skills with the capacity to multitask
                and meet deadlines.
              </li>
            </ul>
          </li>
        </ul>
        <h2>Preferred Qualifications</h2>
        <ul>
          <li>
            Experience with eCommerce platforms (e.g., Shopify, BigCommerce,
            WooCommerce, Amazon).
          </li>
          <li>
            Familiarity with email marketing platforms (e.g., Mailchimp,
            Klaviyo).
          </li>
          <li>
            Certifications in Google Analytics, Google Ads, or similar industry
            programs.
          </li>
        </ul>
      </div>
    ),
    slug: 'digital-marketing-specialist',
  },
]
